import { fetch } from '../../utils';
import Backend from '../../backend';

/** ClientQuiz endpoints (docs/specs/2026-09-24-demo-localstorage-to-db-design.md §17). */

export type QuizType = 'cover' | 'career';
export type QuizPart = 1 | 2;

export type AdviserQuiz = {
  id: string;
  type: QuizType;
  part: QuizPart;
  status: 'sent' | 'completed';
  expired: boolean;
  expiresAt: string;
  displayName: string;
  /** Only while unanswered — the dialog shows the QR code again. */
  token?: string;
  version?: number;
  answers?: Record<string, unknown>;
  submittedAt?: string;
  goalsAddedAt?: string;
  /** Career only: when 第二部分 (`answers.part2`) was answered. */
  partTwoAt?: string;
  appliedAt?: string;
  discAppliedAt?: string;
  /** The language the link opens in (zh-Hant-HK | zh | en), when the adviser chose one. */
  locale?: string;
  createdAt: string;
};

export type ClientQuizList = { completed: AdviserQuiz | null; pending: AdviserQuiz[] };

export type PublicQuiz = {
  type: QuizType;
  part: QuizPart;
  version: number | null;
  status: 'sent' | 'completed';
  expired: boolean;
  displayName: string;
  adviserName: string;
  /** Career part 2 only: the DISC letter of the candidate's own part-1 result, when there is one. */
  disc?: string;
  /** The language the adviser chose for the link (zh-Hant-HK | zh | en); the page starts in it. */
  locale?: string;
};

export class ClientQuizError extends Error {
  status: number;
  code?: string;
  constructor(status: number, code?: string) {
    super(code || `client-quiz ${status}`);
    this.status = status;
    this.code = code;
  }
}

const json = (method: string, body?: unknown) => ({
  method,
  headers: { 'Content-Type': 'application/json' },
  ...(body === undefined ? {} : { body: JSON.stringify(body) })
});

/**
 * The fetch wrapper resolves 2xx/422/500 and throws the rest; both end here as payload or
 * ClientQuizError. `includeCredentials` is only passed through (as `false`) for the public,
 * unauthenticated endpoints — everything else relies on the wrapper's own `= true` default.
 */
const call = async (method: string, options: RequestInit, includeCredentials?: false): Promise<any> => {
  try {
    const url = Backend.methodUrl(method);
    const response = includeCredentials === false ? await fetch(url, options, false) : await fetch(url, options);
    const payload = await response.json();
    if (!payload || payload.success !== true) {
      throw new ClientQuizError(response.status || 500, payload && payload.code);
    }
    return payload;
  } catch (error: any) {
    if (error instanceof ClientQuizError) throw error;
    throw new ClientQuizError(typeof error?.status === 'number' ? error.status : 0, error?.code);
  }
};

export const createClientQuiz = async (input: {
  factsheetId: string;
  type: QuizType;
  part: QuizPart;
  displayName: string;
  locale?: string;
}): Promise<AdviserQuiz> => (await call('client-quizzes', json('POST', input))).quiz;

export const listClientQuizzes = async (factsheetId: string, type: QuizType): Promise<ClientQuizList> => {
  const payload = await call(`factsheets/${encodeURIComponent(factsheetId)}/client-quizzes?type=${type}`, {
    method: 'GET'
  });
  return { completed: payload.completed || null, pending: payload.pending || [] };
};

export const markClientQuizApplied = async (id: string, disc = false): Promise<AdviserQuiz> =>
  (await call(`client-quizzes/${encodeURIComponent(id)}/applied`, json('POST', disc ? { disc: true } : {}))).quiz;

export const deleteClientQuiz = async (id: string, status: 'sent' | 'completed'): Promise<void> => {
  await call(`client-quizzes/${encodeURIComponent(id)}?status=${status}`, { method: 'DELETE' });
};

export const getPublicQuiz = async (token: string): Promise<PublicQuiz> =>
  (await call(`public/client-quizzes/${encodeURIComponent(token)}`, { method: 'GET' }, false)).quiz;

export const submitPublicQuiz = async (
  token: string,
  body: { answers: Record<string, unknown>; version: number; locale?: string }
): Promise<void> => {
  await call(`public/client-quizzes/${encodeURIComponent(token)}/submit`, json('POST', body), false);
};

export const addPublicQuizGoals = async (token: string, goals: Record<string, unknown>): Promise<void> => {
  await call(`public/client-quizzes/${encodeURIComponent(token)}/goals`, json('POST', { goals }), false);
};

/**
 * The link the client gets. Short on purpose — it is pasted into chats and
 * printed as a QR code: `/web/b/<token>` 保障小測驗 (b = business), `/web/r/<token>` 招募測驗,
 * played at that address (views/CoverQuiz/ShortQuizLink.tsx); bare `/b`, `/r` are the previews.
 * `/web/` keeps it in the browser: the iOS app claims every path but that one
 * (public/.well-known/apple-app-site-association).
 */
export const QUIZ_LINK_PREFIX: Record<QuizType, string> = { cover: 'b', career: 'r' };

export const quizLinkOf = (origin: string, type: QuizType, token: string) =>
  `${origin}/web/${QUIZ_LINK_PREFIX[type]}/${encodeURIComponent(token)}`;
